<template>
  <div class="report-page-wrapper m-left-page">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />
    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Header Card -->
    <v-card flat class="header-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <div class="d-flex align-center">
          <div class="header-icon-box mr-3">
            <v-icon color="#0f172a" size="22">mdi-clipboard-text-clock-outline</v-icon>
          </div>
          <div>
            <div class="page-title">DAILY SUMMARY</div>
            <div class="page-subtitle">Consolidated transaction summary for the selected date</div>
          </div>
        </div>

        <div class="d-flex flex-wrap align-center" style="gap: 10px;">
          <v-menu
            ref="menu"
            v-model="menu"
            :close-on-content-click="false"
            :return-value.sync="date"
            transition="scale-transition"
            offset-y
            min-width="auto"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="date"
                label="Date"
                outlined
                background-color="white"
                readonly
                hide-details
                dense
                class="pro-input date-input"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="date"
              no-title
              scrollable
              color="#9a6b27"
              @change="$refs.menu.save(date); fetchSummaryData()"
            >
              <v-spacer></v-spacer>
            </v-date-picker>
          </v-menu>

          <v-btn
            dark
            color="#9a6b27"
            class="btn-primary text-capitalize elevation-1"
            @click="downloadSummary()"
          >
            <v-icon left small>mdi-download-outline</v-icon>
            Download PDF
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- KPI Overview Cards -->
    <div class="kpi-grid mb-4">
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">OPENING BALANCE</div>
          <div class="kpi-value">₹{{ (openingBalance || 0).toFixed(2) }}</div>
          <div class="kpi-hint">Balance carried forward</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f1f5f9; color: #475569;">
          <v-icon size="20" color="#475569">mdi-wallet-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL RECEIPTS</div>
          <div class="kpi-value">₹{{ (totalReceipts || 0).toFixed(2) }}</div>
          <div class="kpi-hint">Money received today</div>
        </div>
        <div class="kpi-icon-pill" style="background: #f0fdf4; color: #16a34a;">
          <v-icon size="20" color="#16a34a">mdi-arrow-down-bold-circle-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL PAYMENTS</div>
          <div class="kpi-value">₹{{ (totalPayments || 0).toFixed(2) }}</div>
          <div class="kpi-hint">Money paid out today</div>
        </div>
        <div class="kpi-icon-pill" style="background: #fef2f2; color: #dc2626;">
          <v-icon size="20" color="#dc2626">mdi-arrow-up-bold-circle-outline</v-icon>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">GROSS BALANCE</div>
          <div class="kpi-value">₹{{ (grossBalance || 0).toFixed(2) }}</div>
          <div class="kpi-hint">Closing balance for the day</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="20" color="#2563eb">mdi-scale-balance</v-icon>
        </div>
      </div>
    </div>

    <!-- Data Table Card -->
    <v-card flat class="content-table-card rounded-lg overflow-hidden">
      <div v-if="summaryData && summaryData.length > 0" style="overflow-x: auto;">
        <v-simple-table class="pro-master-table m-stack-table m-stack-grid">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="min-width: 170px;">Description</th>
                <th class="text-right">Gross Wt</th>
                <th class="text-right">Stone Wt</th>
                <th class="text-right">Stone Rate</th>
                <th class="text-right">Metal Rate</th>
                <th class="text-right">Avg Rate</th>
                <th class="text-right">GST Coll</th>
                <th class="text-right">M.C</th>
                <th class="text-right">Avg MC%</th>
                <th class="text-right">Receipt</th>
                <th class="text-right">Payments</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in summaryData" :key="i" class="master-table-row">
                <td data-label="Description" class="font-weight-semibold" style="color: #0f172a;">{{ row.description || '—' }}</td>
                <td data-label="Gross Wt" class="text-right">{{ (row.grossWt || 0).toFixed(2) }}</td>
                <td data-label="Stone Wt" class="text-right">{{ (row.stoneWt || 0).toFixed(2) }}</td>
                <td data-label="Stone Rate" class="text-right">{{ (row.stoneRate || 0).toFixed(2) }}</td>
                <td data-label="Metal Rate" class="text-right">{{ (row.metalRate || 0).toFixed(2) }}</td>
                <td data-label="Avg Rate" class="text-right">{{ (row.averageRate || 0).toFixed(2) }}</td>
                <td data-label="GST Coll" class="text-right">{{ (row.gstColl || 0).toFixed(2) }}</td>
                <td data-label="M.C" class="text-right">{{ (row.mc || 0).toFixed(2) }}</td>
                <td data-label="Avg MC%" class="text-right">{{ (row.averageMCPercent || 0).toFixed(2) }}</td>
                <td data-label="Receipt" class="text-right font-weight-semibold" style="color: #16a34a;">{{ (row.receipt || 0).toFixed(2) }}</td>
                <td data-label="Payments" class="text-right font-weight-semibold" style="color: #dc2626;">{{ (row.payment || 0).toFixed(2) }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-wrap py-12 text-center">
        <div class="empty-icon-circle mb-3">
          <v-icon size="36" color="#94a3b8">mdi-clipboard-text-clock-outline</v-icon>
        </div>
        <div class="empty-title">No summary data</div>
        <div class="empty-subtitle">
          No transactions were recorded for the selected date.
        </div>
      </div>
    </v-card>
  </div>
</template>
<script>
import axios from "axios";
export default {
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2000,
      msg: null,
      menu: false,
      date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      summaryData: [],
      openingBalance: 0,
      totalReceipts: 0,
      totalPayments: 0,
      grossBalance: 0,
    };
  },
  mounted() {
    this.fetchSummaryData();
  },
  methods: {
    fetchSummaryData() {
      if (!this.date) return;
      this.appLoading = true;
      axios({
        method: "get",
        url: "/data/dailysummary",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          date: this.date,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            this.summaryData = response.data.data.rows;
            this.openingBalance = response.data.data.openingBalance;
            this.totalReceipts = response.data.data.totalReceipts;
            this.totalPayments = response.data.data.totalPayments;
            this.grossBalance = response.data.data.grossBalance;
          } else {
            this.msg = response.data.msg || "Failed to fetch data";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    downloadSummary() {
      if (!this.date) {
        this.msg = "Please choose a date";
        this.showsnackbar = true;
        return;
      }
      this.appLoading = true;
      axios({
        method: "get",
        url: "/download/dailysummary",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          date: this.date,
        },
        responseType: "blob",
      })
        .then((response) => {
          this.appLoading = false;
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "DailySummary.pdf");
          document.body.appendChild(link);
          link.click();
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap");

.report-page-wrapper {
  padding: 4px 0 40px 0;
  width: 100%;
  margin: 0 auto;
  font-family: "Plus Jakarta Sans", "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  color: #0f172a;
}

.header-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.header-icon-box {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.page-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
}

.date-input {
  width: 160px;
}

.pro-input :deep(.v-input__slot) {
  min-height: 38px !important;
  border-radius: 8px !important;
  font-size: 13px !important;
}

.pro-input :deep(fieldset) {
  border-color: #cbd5e1 !important;
}

.pro-input.v-input--is-focused :deep(fieldset) {
  border-color: #9a6b27 !important;
  border-width: 1.5px !important;
}

.btn-primary {
  background: linear-gradient(135deg, #b9893f 0%, #875c24 100%) !important;
  background-color: #9a6b27 !important;
  color: #ffffff !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  height: 38px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(154, 106, 44, 0.28) !important;
  transition: all 0.2s ease !important;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #c59449 0%, #956627 100%) !important;
  box-shadow: 0 6px 18px rgba(154, 106, 44, 0.38) !important;
  transform: translateY(-1.5px);
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.kpi-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.kpi-label {
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  margin: 3px 0;
  line-height: 1.2;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
}

.kpi-icon-pill {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Data Table Card */
.content-table-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02) !important;
}

.pro-master-table table {
  width: 100%;
  border-collapse: collapse;
}

.pro-master-table th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: nowrap !important;
  vertical-align: middle !important;
  height: 44px !important;
}

.pro-master-table td {
  font-size: 13px !important;
  color: #334155 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  white-space: nowrap !important;
  vertical-align: middle !important;
  height: 48px !important;
}

.pro-master-table td.text-right,
.pro-master-table th.text-right {
  text-align: right !important;
}

.pro-master-table td.text-left,
.pro-master-table th.text-left {
  text-align: left !important;
}

.master-table-row:hover {
  background-color: #f8fafc !important;
}

/* Empty State */
.empty-state-wrap {
  padding: 60px 20px;
}

.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}
</style>
